import { useCallback, useMemo } from "react";
import { Formik, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, Select, SubmitButton, InputNumber } from "formik-antd";
import { useTranslation } from "react-i18next";
import { WorkflowCriteria, Report } from "/app/src/models";
import { simpleSchemaBuilder } from "/app/src/helpers";
import { Button } from "@heroui/react";
import SelectWithSkeleton from "/app/src/components/generic/components/selectWithSkeleton";
import DividerWithText from "/app/src/components/HeroUi/DividerWithText";

interface FormValues {
  reportId: number | undefined;
  value: string | undefined;
  verb: string | undefined;
}

/**
 * Represents the criteria form for workflow.
 *
 * @param {Object} props - The component props.
 * @param {WorkflowCriteria} props.criteria - The workflow criteria.
 * @param {function} props.removeWorkflowCriteria - Function to remove workflow criteria.
 * @param {function} props.updateWorkflowCriteria - Function to update workflow criteria.
 * @param {Report[]} props.reports - Array of report items.
 * @param {boolean} props.isFetchingReports - Flag indicating if reports are being fetched.
 * @returns {JSX.Element} Rendered criteria form component.
 */
export default function Criteria({
  criteria,
  removeWorkflowCriteria,
  updateWorkflowCriteria,
  reports,
  isFetchingReports,
}: {
  criteria: WorkflowCriteria;
  removeWorkflowCriteria: (
    criteria: WorkflowCriteria,
  ) => Promise<any> | undefined;
  updateWorkflowCriteria: (criteria: WorkflowCriteria) => Promise<any>;
  reports: Report[];
  isFetchingReports: boolean;
}) {
  const { t } = useTranslation();
  const verbs = useMemo(
    () => [
      { name: "equals", value: "eq" },
      { name: "not", value: "not" },
      { name: "greater than", value: "gt" },
      { name: "less than", value: "lt" },
      { name: "greater or equal", value: "ge" },
      { name: "less or equal", value: "le" },
    ],
    [],
  );

  /**
   * Handles updating the workflow criteria.
   *
   * @param {Object} values - Form values.
   * @param {Object} actions - Formik actions.
   * @returns {void}
   */
  const updateWorkflowCriteriaHandler = useCallback(
    (values, actions) => {
      if (criteria?.id) {
        updateWorkflowCriteria({ id: criteria.id, ...values }).then(
          (response) => {
            if (!response.errors) {
              actions.resetForm();
            }
          },
        );
      }
    },
    [criteria.id, updateWorkflowCriteria],
  );

  /**
   * Handles deletion of the workflow criteria.
   *
   * @returns {void}
   */
  const deleteWorkflowCriteriaHandler = useCallback(() => {
    removeWorkflowCriteria(criteria);
  }, [removeWorkflowCriteria, criteria]);

  /**
   * Renders options for the report selection.
   *
   * @returns {JSX.Element[]} Array of rendered option elements.
   */
  const renderOptions = useCallback(() => {
    return reports.map((c) => (
      <Select.Option key={c.id} value={c.id}>
        {c.name}
      </Select.Option>
    ));
  }, [reports]);

  /**
   * Renders the criteria form.
   *
   * @param {FormikProps<FormValues>} props - Formik properties including form state.
   * @returns {JSX.Element} Rendered form component.
   */
  const critieriaForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty }) => (
        <Form>
          <Row justify="start" align="middle" gutter={16}>
            <Col span={5}>
              <h4>{t("translation:report")}</h4>
              <Form.Item name="reportId">
                <SelectWithSkeleton
                  loading={isFetchingReports}
                  placeholder={t("translation:select_report")}
                  name="reportId"
                  showSearch
                  optionFilterProp="label"
                  renderOptions={renderOptions}
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              <span className="blockText">{t("translation:row_count")}</span>
            </Col>
            <Col span={5}>
              <h4>{t("translation:comparison")}</h4>
              <Form.Item name="verb">
                <Select
                  name="verb"
                  size="large"
                  placeholder={t("translation:select_comparison")}
                >
                  {verbs.map((c) => (
                    <Select.Option key={c.value} value={c.value}>
                      {c.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={3}>
              <h4>{t("translation:value")}</h4>
              <Form.Item name="value" hasFeedback={false}>
                <InputNumber name="value" min={0} max={1000000} size="large" />
              </Form.Item>
            </Col>
            <>
              <Col span={2}>
                <SubmitButton
                  type="primary"
                  size="large"
                  block
                  disabled={!dirty}
                >
                  {t("translation:save")}
                </SubmitButton>
              </Col>
              <Col span={3}>
                <Button
                  size="md"
                  radius="none"
                  variant="bordered"
                  color="default"
                  fullWidth
                  onPress={deleteWorkflowCriteriaHandler}
                  className="hover:border-primary-default hover:text-primary-default bg-white"
                >
                  {t("translation:remove")}
                </Button>
              </Col>
            </>
          </Row>
        </Form>
      ),
      [
        t,
        isFetchingReports,
        renderOptions,
        verbs,
        deleteWorkflowCriteriaHandler,
      ],
    );

  return (
    <div>
      <Formik
        component={critieriaForm}
        enableReinitialize
        initialValues={{
          reportId: criteria?.reportId,
          verb: criteria.verb,
          value: criteria.value,
        }}
        validationSchema={simpleSchemaBuilder([
          { name: "value", type: "number", required: true },
        ])}
        onSubmit={updateWorkflowCriteriaHandler}
      />{" "}
      <DividerWithText>And</DividerWithText>
    </div>
  );
}
